import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApiEndpoint, CdnDashboardFilter, DashboardEndpoint, FormActionType, FormType } from '@enums';

import type {
  FilterState,
  FormMetadata,
  FormPayload,
  FormSliceState,
  FormState,
  PaginationProps,
  SortableFormItem,
} from '@types';
import { extractFormIds, initializeStateStructure, validateSameArray } from '@utils';

export interface UpdateSelectedIdsPayload extends FormMetadata {
  id: string | string[];
}

export interface FormRowData {
  id: string;
  [key: string]: unknown;
}

export interface BulkSelectionPayload extends FormMetadata {
  filterFn?: (item: FormRowData) => boolean;
}

const CDN_FORMS = {
  pagination: {
    [ApiEndpoint.DEFAULT]: {
      page_size: 5,
    },
  },
};

const CDN_DASHBOARD_FILTER: { filter: FilterState } = {
  filter: {
    searchTerm: '',
    filterCriteria: [
      {
        from: DashboardEndpoint.TOTAL_TRAFFIC,
        label: CdnDashboardFilter.Time,
        value: '15m',
      },
      {
        from: DashboardEndpoint.TOTAL_TRAFFIC,
        label: CdnDashboardFilter.Domain,
        value: 'all',
      },
    ],
  },
};

const formConfigs = new Map<FormType, Partial<FormState>>([
  [FormType.CdnOrigin, { ...CDN_FORMS }],
  [FormType.CdnRoute, { ...CDN_FORMS }],
  [FormType.CdnDashboard, { ...CDN_DASHBOARD_FILTER }],
]);

const defaultFormState: FormState = {
  init: true,
  loading: {},
  submitting: {},
  sortLoading: false,
  saveSuccess: false,
  deleteSuccess: false,
  formList: { [ApiEndpoint.DEFAULT]: [] },
  formContent: { [ApiEndpoint.DEFAULT]: {} },
  sortingList: [],
  filter: {
    searchTerm: '',
    filterCriteria: [{ label: 'status', value: 'all' }],
  },
  selectedIds: { [ApiEndpoint.DEFAULT]: [] },
  pagination: {
    [ApiEndpoint.DEFAULT]: {
      first_page: 1,
      last_page: 1,
      next_page: 1,
      page: 1,
      total: 0,
      total_pages: 0,
      page_size: 5,
    },
  },
  procedure: FormActionType.Idle,
  error: { [ApiEndpoint.DEFAULT]: null },
  lastUpdated: 0,
};

const initialState: FormSliceState = Object.values(FormType).reduce((acc, type) => {
  const customConfig = formConfigs.get(type as FormType);

  acc[type as FormType] = {
    ...defaultFormState,
    ...(customConfig && {
      filter: {
        ...defaultFormState.filter,
        ...customConfig.filter,
      },
      pagination: {
        ...defaultFormState.pagination,
        ...customConfig.pagination,
      },
    }),
  };

  return acc;
}, {} as FormSliceState);

const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    fetchFormList: (state, { payload }: PayloadAction<FormPayload>) => {
      const { formType, globalOptions: { endpoint = ApiEndpoint.DEFAULT } = {} } = payload;
      state[formType].loading[endpoint] = true;
      state[formType].formList = {
        ...state[formType].formList,
        [endpoint]: state[formType].formList?.[endpoint] ?? [],
      };
    },
    fetchFormListSuccess: (
      state,
      {
        payload,
      }: PayloadAction<
        FormMetadata & {
          content?: boolean;
          lastUpdated: number;
          unique?: boolean;
          uniqueId?: string | number;
          list: any[];
          paginationInfo?: PaginationProps;
        }
      >,
    ) => {
      const { formType, lastUpdated, endpoint, unique, uniqueId, content = false, list, paginationInfo } = payload;
      state[formType].init = false;
      if (!endpoint) {
        return;
      }
      state[formType].loading[endpoint] = false;
      const targetStore = content ? 'formContent' : 'formList';
      state[formType][targetStore][endpoint] =
        unique && uniqueId
          ? {
              ...state[formType][targetStore][endpoint],
              [uniqueId]: list,
            }
          : list;
      state[formType].pagination[endpoint] = {
        ...state[formType].pagination[endpoint],
        ...paginationInfo,
      };
      state[formType].lastUpdated = lastUpdated;
    },
    fetchFormListError: (state, { payload }: PayloadAction<FormMetadata & { responseResult: any }>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, responseResult } = payload;
      if (state[formType] && endpoint) {
        state[formType].init = false;
        state[formType].loading[endpoint] = false;
        state[formType].error[endpoint] = responseResult;
      }
    },
    fetchDashboardGraph: (state, { payload }: PayloadAction<any>) => {},
    updateSortingList(state, { payload }: PayloadAction<FormMetadata & { list: SortableFormItem[] }>) {
      const { formType, list } = payload;
      if (state[formType]) {
        state[formType].sortingList = list.map((item, index) => ({
          id: item.id,
          name: item.name,
          sort_order: index + 1,
          original_id: item.original_id,
        }));
      }
    },
    fetchSortingListSuccess(state, { payload }: PayloadAction<FormMetadata & { responseResult: any }>) {
      const { formType, endpoint = ApiEndpoint.DEFAULT, responseResult } = payload;
      if (state[formType] && endpoint) {
        state[formType].loading[endpoint] = false;
        state[formType].sortingList = responseResult;
      }
    },
    submitFormRequest: (state, { payload }: PayloadAction<FormPayload>) => {
      const { formType, globalOptions: { endpoint = ApiEndpoint.DEFAULT } = {} } = payload;
      state[formType].submitting[endpoint] = true;
    },
    submitFormSuccess: (state, { payload }: PayloadAction<FormMetadata & { content?: typeof defaultFormState }>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, content = defaultFormState.formContent } = payload;
      if (state[formType] && endpoint) {
        state[formType].error[endpoint] = null;
        state[formType].submitting[endpoint] = false;
        state[formType].formContent[endpoint] = content;
        state[formType].saveSuccess = true;
      }
    },
    submitFormError: (state, { payload }: PayloadAction<FormMetadata & { responseResult: any }>) => {
      const { formType, endpoint, responseResult } = payload;
      if (state[formType] && endpoint) {
        state[formType].submitting[endpoint] = false;
        state[formType].error[endpoint] = responseResult;
      }
    },
    updateFormFilter: (state, { payload }: PayloadAction<FormMetadata & { value: any }>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, value } = payload;

      const remainingCriteria = state[formType].filter.filterCriteria.filter(
        (criterion) => criterion.from !== endpoint,
      );

      state[formType].filter.filterCriteria = [...remainingCriteria, ...value];
    },
    updateFormContent: (state, { payload }: PayloadAction<FormMetadata & { content: any }>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, content } = payload;
      state[formType].formContent[endpoint] = content;
    },
    updateFormProcedure: (state, { payload }: PayloadAction<FormMetadata & { procedure: FormActionType }>) => {
      const { formType, procedure } = payload;
      state[formType].procedure = procedure;
    },
    updateFormPages: (state, { payload }: PayloadAction<FormPayload & { page: number }>) => {
      const { formType, globalOptions = {}, page } = payload;
      const endpoint = globalOptions.endpoint ?? ApiEndpoint.DEFAULT;
      state[formType].pagination[endpoint] = {
        ...state[formType].pagination[endpoint],
        page,
      };
      state[formType].selectedIds[endpoint] = [];
    },
    updateFormPageSize: (state, { payload }: PayloadAction<FormPayload & { page_size: number }>) => {
      const { formType, globalOptions = {}, page_size } = payload;
      const endpoint = globalOptions.endpoint ?? ApiEndpoint.DEFAULT;
      state[formType].pagination[endpoint] = {
        ...state[formType].pagination[endpoint],
        page_size,
        page: 1,
      };
    },
    updateSelectedIds: (state, { payload }: PayloadAction<UpdateSelectedIdsPayload>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, id } = payload;
      initializeStateStructure({
        state,
        formType: FormType.Cdn,
        endpoint: ApiEndpoint.DEFAULT,
        properties: {
          selectedIds: [],
        },
      });
      const currentSelected = state[formType].selectedIds[endpoint] ?? [];
      if (Array.isArray(id)) {
        const isSameSelection = validateSameArray(id, currentSelected);
        state[formType].selectedIds[endpoint] = isSameSelection ? [] : [...id];
      } else {
        const selectedSet = new Set(currentSelected);
        if (selectedSet.has(id)) {
          selectedSet.delete(id);
        } else {
          selectedSet.add(id);
        }
        state[formType].selectedIds[endpoint] = Array.from(selectedSet);
      }
    },
    toggleCheckBox: (state, { payload }: PayloadAction<BulkSelectionPayload>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, filterFn } = payload;
      initializeStateStructure({
        state,
        formType: FormType.Cdn,
        endpoint: ApiEndpoint.DEFAULT,
        properties: {
          selectedIds: [],
        },
      });
      const formList = state[formType].formList[endpoint] || [];
      const allIds = extractFormIds(formList, filterFn);
      const currentSelected = state[formType].selectedIds[endpoint] ?? [];

      const allSelected = allIds.length > 0 && validateSameArray(allIds, currentSelected);

      state[formType].selectedIds[endpoint] = allSelected ? [] : allIds;
    },
    clearCheckBox: (state, { payload }: PayloadAction<BulkSelectionPayload>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT } = payload;
      initializeStateStructure({
        state,
        formType: FormType.Cdn,
        endpoint: ApiEndpoint.DEFAULT,
        properties: {
          selectedIds: [],
        },
      });
      state[formType].selectedIds[endpoint] = [];
    },
    deleteFormContent: (state, { payload }: PayloadAction<FormPayload>) => {
      const { formType, globalOptions: { endpoint = ApiEndpoint.DEFAULT } = {} } = payload;
      if (state[formType]) {
        state[formType].loading[endpoint] = true;
      }
    },
    deleteFormContentError: (state, { payload }: PayloadAction<FormMetadata & { error: any }>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT, error } = payload;
      if (state[formType] && endpoint) {
        state[formType].loading[endpoint] = false;
        state[formType].error[endpoint] = error;
      }
    },
    resetSubmitFormState: (state, { payload }: PayloadAction<FormMetadata>) => {
      const { formType, endpoint = ApiEndpoint.DEFAULT } = payload;

      if (state[formType]) {
        state[formType].submitting = defaultFormState.submitting;
        state[formType].saveSuccess = defaultFormState.saveSuccess;

        if (endpoint) {
          state[formType].error[endpoint] = defaultFormState.error[endpoint];
        }
      }
    },
    resetFormPagination: (state, { payload }: PayloadAction<FormType>) => {
      if (state[payload]) {
        state[payload].pagination = initialState[payload].pagination;
      }
    },
    resetFormData: (state, { payload }: PayloadAction<any>) => {
      const { uniqueId, formType, endpoint = ApiEndpoint.DEFAULT, resetType = 'list' } = payload;

      if (uniqueId) {
        if (resetType === 'content' || resetType === 'both') {
          if (!state[formType].formContent[endpoint]) {
            state[formType].formContent[endpoint] = {};
          }
          if (typeof state[formType].formContent[endpoint] === 'object') {
            state[formType].formContent[endpoint][uniqueId] = {};
          }
        }
        if (resetType === 'list' || resetType === 'both') {
          if (!state[formType].formList[endpoint]) {
            state[formType].formList[endpoint] = {};
          }
          if (typeof state[formType].formList[endpoint] === 'object') {
            state[formType].formList[endpoint][uniqueId] = [];
          }
        }
      } else {
        if (resetType === 'content' || resetType === 'both') {
          state[formType].formContent[endpoint] = {};
        }
        if (resetType === 'list' || resetType === 'both') {
          state[formType].formList[endpoint] = {};
        }
      }
    },
    resetDashboardFilter: (state, { payload }: PayloadAction<FormType>) => {
      if (state[payload]) {
        state[payload].filter = CDN_DASHBOARD_FILTER.filter;
      }
    },
    resetFormFilter: (state, { payload }: PayloadAction<any>) => {
      const { formType } = payload;
      state[formType].filter = {
        searchTerm: '',
        filterCriteria: [{ label: 'status', value: 'all' }],
      };
    },
    resetCurrentForm: (state, { payload }: PayloadAction<FormType>) => {
      state[payload] = { ...defaultFormState };
    },
    resetAllForms: () => initialState,
    cancelAutoReload: () => {},
  },
});

export const {
  fetchFormList,
  fetchFormListSuccess,
  fetchFormListError,
  fetchDashboardGraph,
  fetchSortingListSuccess,
  submitFormRequest,
  submitFormSuccess,
  submitFormError,
  clearCheckBox,
  updateSortingList,
  resetFormPagination,
  updateFormFilter,
  toggleCheckBox,
  updateFormContent,
  updateFormProcedure,
  updateFormPages,
  updateFormPageSize,
  updateSelectedIds,
  deleteFormContent,
  deleteFormContentError,
  resetSubmitFormState,
  resetCurrentForm,
  resetDashboardFilter,
  resetFormData,
  resetFormFilter,
  resetAllForms,
  cancelAutoReload,
} = formSlice.actions;

export default formSlice.reducer;
